import {
  INSURANCE_PROGRAMS_ADDITIONAL_INFORMATION,
  INSURANCE_PROGRAMS_COUNTRY,
  INSURANCE_PROGRAMS_ENROLLMENT_CODE,
  INSURANCE_PROGRAMS_PLAN_IDENTIFIER,
} from '@constants/extension-codes';

import { formatFhirDate } from '@utils/fhir';

import type { CoverageModel, CoverageModelInput } from 'generated/types';
import { CoverageStatus } from 'generated/types';

import { FhirResource, type BaseFhirFormValues } from './fhir-resource';

export type InsuranceValues = Pick<
  InsurancePlan,
  | 'additionalInformation'
  | 'country'
  | 'enrollmentCode'
  | 'expirationDate'
  | 'issueDate'
  | 'planIdentifier'
  | 'planNumber'
  | 'provider'
> & {
  associatedResources?: string[];
};

export type FormValues = BaseFhirFormValues & InsuranceValues;

export class InsurancePlan extends FhirResource {
  protected model: CoverageModel;

  public constructor(model: CoverageModel) {
    super(model);
    this.model = model;
  }

  public get provider() {
    return this.model.payor.map(({ display }) => display).join('/');
  }

  public set provider(value: string | null | undefined) {
    this.model = {
      ...this.model,
      ...(value && {
        payor: [
          {
            display: value,
          },
        ],
      }),
    };
  }

  public get planNumber() {
    return this.model.subscriberId;
  }

  public set planNumber(value: string | null | undefined) {
    this.model = {
      ...this.model,
      subscriberId: value,
    };
  }

  public get planIdentifier() {
    const extension = this.findExtension(INSURANCE_PROGRAMS_PLAN_IDENTIFIER);
    return extension?.valueString;
  }

  public set planIdentifier(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: INSURANCE_PROGRAMS_PLAN_IDENTIFIER,
        valueString: value,
      });
    } else {
      this.removeExtension(INSURANCE_PROGRAMS_PLAN_IDENTIFIER);
    }
  }

  public get enrollmentCode() {
    const extension = this.findExtension(INSURANCE_PROGRAMS_ENROLLMENT_CODE);
    return extension?.valueString;
  }

  public set enrollmentCode(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: INSURANCE_PROGRAMS_ENROLLMENT_CODE,
        valueString: value,
      });
    } else {
      this.removeExtension(INSURANCE_PROGRAMS_ENROLLMENT_CODE);
    }
  }

  public get country() {
    const extension = this.findExtension(INSURANCE_PROGRAMS_COUNTRY);
    return extension?.valueString;
  }

  public set country(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: INSURANCE_PROGRAMS_COUNTRY,
        valueString: value,
      });
    } else {
      this.removeExtension(INSURANCE_PROGRAMS_COUNTRY);
    }
  }

  public get additionalInformation() {
    const extension = this.findExtension(
      INSURANCE_PROGRAMS_ADDITIONAL_INFORMATION
    );
    return extension?.valueString;
  }

  public set additionalInformation(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: INSURANCE_PROGRAMS_ADDITIONAL_INFORMATION,
        valueString: value,
      });
    } else {
      this.removeExtension(INSURANCE_PROGRAMS_ADDITIONAL_INFORMATION);
    }
  }

  public get issueDate() {
    const { period } = this.model;
    if (!period) {
      return null;
    }
    return period.start;
  }

  public set issueDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      period: {
        ...this.model.period,
        start: value,
      },
    };
  }

  public get expirationDate() {
    const { period } = this.model;
    if (!period) {
      return null;
    }
    return period.end;
  }

  public set expirationDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      period: {
        ...this.model.period,
        end: value,
      },
    };
  }

  public getIssueDateStr() {
    return formatFhirDate(this.issueDate, { showTime: false });
  }

  public getExpiryDateStr() {
    return formatFhirDate(this.expirationDate, { showTime: false });
  }

  public getModelInput(values?: InsuranceValues): CoverageModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    return {
      ...cleaned,
      status: CoverageStatus.active,
    };
  }
}
